import apiClient from '../axios';
import {
  AdPosition,
  AdResponse,
  AdEventRequest,
  AdminAdListItem,
  AdminAdDetail,
  CreateAdRequest,
  PositionScheduleItem,
  ReservePositionRequest,
  UpdatePositionRequest,
  AdStats,
  AdStatsSummary,
  AdStatsListItem,
  AdType,
  AdPlatform,
  RewardAdFormat,
  AdActionStats,
  WatchHistoryResponse,
  AdWatchLocation,
  RotationItem,
  UpdateWeightsRequest,
  PositionConfig,
  UpdatePositionConfigRequest,
  OpsSnapshot,
  DeploymentMarker,
  CreateDeploymentMarkerRequest,
  IdAggregation,
  AcquisitionData,
  ActivationData,
  RetentionData,
  RetentionType,
  DailyCohortRetentionResponse,
  CohortUserItem,
  UserRetentionDetail,
  AdTrendResponse,
  StrategyMetricsResponse,
  TossDataRequest,
  DeployMarkerRequest,
  DeployMarkerItem,
  RewardPerformanceResponse,
  TouchpointDailyMatrixResponse,
  RewardBreakdownResponse,
  EcpmUserStatsResponse,
  RewardAdConfig,
  UpdateRewardAdConfigRequest,
  PieceSnapshotResponse,
  SharelinkResolveResponse,
} from '../types/Ad.api.type';

/**
 * 광고 관련 API 모음
 */
const ADS_URL = () => '/api/ads';
const ADMIN_ADS_URL = () => '/api/admin/ads';
const ANALYTICS_URL = () => '/api/admin/dashboard';

export const adApi = {
  // ==================== 사용자 API ====================

  /** 위치별 활성 광고 조회 */
  getAdsByPosition: (position: AdPosition) => {
    return apiClient.get<AdResponse[]>(`${ADS_URL()}`, {
      params: { position },
    });
  },

  /** 광고 이벤트 기록 */
  recordEvent: (params: AdEventRequest) => {
    return apiClient.post(`${ADS_URL()}/events`, params);
  },

  // ==================== 어드민 CRUD ====================

  /** 광고 목록 조회 (필터) */
  getAdList: (type?: AdType, position?: AdPosition, isActive?: boolean) => {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (position) params.append('position', position);
    if (isActive !== undefined) params.append('isActive', String(isActive));

    const queryString = params.toString();
    return apiClient.get<AdminAdListItem[]>(`${ADMIN_ADS_URL()}${queryString ? `?${queryString}` : ''}`);
  },

  /** 광고 상세 조회 */
  getAdDetail: (id: number) => {
    return apiClient.get<AdminAdDetail>(`${ADMIN_ADS_URL()}/${id}`);
  },

  /** 광고 등록 */
  createAd: (params: CreateAdRequest) => {
    return apiClient.post<{ id: number }>(`${ADMIN_ADS_URL()}`, params);
  },

  /** 쉐어링크 「채우기」 — 상품 이미지 · 평점 · 리뷰 수 */
  resolveSharelink: (url: string) => {
    return apiClient.post<SharelinkResolveResponse>(`${ADMIN_ADS_URL()}/sharelink/resolve`, { url });
  },

  /** 광고 수정 */
  updateAd: (id: number, params: CreateAdRequest) => {
    return apiClient.put(`${ADMIN_ADS_URL()}/${id}`, params);
  },

  /** 광고 삭제 */
  deleteAd: (id: number) => {
    return apiClient.delete(`${ADMIN_ADS_URL()}/${id}`);
  },

  // ==================== 통계 ====================

  /** 광고별 통계 (위치별 PV/UV/클릭/CTR) */
  getAdStats: (id: number) => {
    return apiClient.get<AdStats[]>(`${ADMIN_ADS_URL()}/${id}/stats`);
  },

  /** 광고별 시간대/일별 추이 */
  getAdTrend: (id: number) => {
    return apiClient.get<AdTrendResponse>(`${ADMIN_ADS_URL()}/${id}/stats/trend`);
  },

  // ==================== 예약 ====================

  /** 위치별 예약 현황 (타임라인) */
  getPositionSchedule: (position: AdPosition) => {
    return apiClient.get<PositionScheduleItem[]>(`${ADMIN_ADS_URL()}/positions/${position}/schedule`);
  },

  /** 광고 예약 등록 */
  reservePosition: (params: ReservePositionRequest) => {
    return apiClient.post<{ id: number }>(`${ADMIN_ADS_URL()}/positions/reserve`, params);
  },

  /** 예약 수정 */
  updatePosition: (id: number, params: UpdatePositionRequest) => {
    return apiClient.put(`${ADMIN_ADS_URL()}/positions/${id}`, params);
  },

  /** 예약 취소 */
  deletePosition: (id: number) => {
    return apiClient.delete(`${ADMIN_ADS_URL()}/positions/${id}`);
  },

  // ==================== 로테이션 ====================

  /** 위치별 로테이션 현황 */
  getRotation: (position: AdPosition) => {
    return apiClient.get<RotationItem[]>(`${ADMIN_ADS_URL()}/positions/${position}/rotation`);
  },

  /** 가중치 일괄 수정 */
  updateWeights: (params: UpdateWeightsRequest) => {
    return apiClient.put(`${ADMIN_ADS_URL()}/positions/weights`, params);
  },

  // ==================== 어드민 통계 (플랫폼별) ====================

  /** 플랫폼별 통계 요약 */
  getStatsSummary: (platform?: AdPlatform) => {
    const params = new URLSearchParams();
    if (platform) params.append('platform', platform);
    const queryString = params.toString();
    return apiClient.get<AdStatsSummary>(`${ADMIN_ADS_URL()}/stats/summary${queryString ? `?${queryString}` : ''}`);
  },

  /** 플랫폼별 광고별 통계 목록 */
  getStatsList: (platform?: AdPlatform) => {
    const params = new URLSearchParams();
    if (platform) params.append('platform', platform);
    const queryString = params.toString();
    return apiClient.get<AdStatsListItem[]>(`${ADMIN_ADS_URL()}/stats/list${queryString ? `?${queryString}` : ''}`);
  },

  /** 연계 행동 통계 */
  getActionStats: (period?: string, platform?: AdPlatform, adFormat?: RewardAdFormat) => {
    const params = new URLSearchParams();
    if (period) params.append('period', period);
    if (platform) params.append('platform', platform);
    if (adFormat) params.append('adFormat', adFormat);
    const queryString = params.toString();
    return apiClient.get<AdActionStats[]>(`${ADMIN_ADS_URL()}/stats/actions${queryString ? `?${queryString}` : ''}`);
  },

  /** 광고 시청 히스토리 */
  getWatchHistory: (options: {
    page?: number;
    size?: number;
    userId?: number;
    location?: AdWatchLocation;
    platform?: AdPlatform;
    adFormat?: RewardAdFormat;
    startDate?: string;
    endDate?: string;
  }) => {
    const params = new URLSearchParams();
    if (options.page !== undefined) params.append('page', String(options.page));
    if (options.size !== undefined) params.append('size', String(options.size));
    if (options.userId) params.append('userId', String(options.userId));
    if (options.location) params.append('location', options.location);
    if (options.platform) params.append('platform', options.platform);
    if (options.adFormat) params.append('adFormat', options.adFormat);
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);
    const queryString = params.toString();
    return apiClient.get<WatchHistoryResponse>(
      `${ADMIN_ADS_URL()}/watch-history${queryString ? `?${queryString}` : ''}`,
    );
  },

  // ==================== 토스광고 설정 ====================

  /** 위치별 토스광고 설정 전체 조회 */
  getPositionConfigs: () => {
    return apiClient.get<PositionConfig[]>(`${ADMIN_ADS_URL()}/position-config`);
  },

  /** 위치별 토스광고 설정 수정 */
  updatePositionConfig: (position: AdPosition, data: UpdatePositionConfigRequest) => {
    return apiClient.put(`${ADMIN_ADS_URL()}/position-config/${position}`, data);
  },

  /** 터치포인트별 리워드/전면 광고 설정 전체 조회 */
  getRewardAdConfigs: () => {
    return apiClient.get<RewardAdConfig[]>(`${ADMIN_ADS_URL()}/reward-ad-config`);
  },

  /** 터치포인트별 리워드/전면 광고 설정 수정 */
  updateRewardAdConfig: (location: AdWatchLocation, data: UpdateRewardAdConfigRequest) => {
    return apiClient.put(`${ADMIN_ADS_URL()}/reward-ad-config/${location}`, data);
  },

  // ==================== G1-2: 운영 설정 스냅샷 + 배포 타임라인 + 광고ID별 집계 ====================

  /** G1-2: 운영 설정 스냅샷 (섹션 1+2 통합) */
  getOpsSnapshot: () => {
    return apiClient.get<OpsSnapshot>(`${ADMIN_ADS_URL()}/ops-snapshot`);
  },

  /** G1-2: 배포 타임라인 마커 조회 */
  getDeploymentMarkers: (from: string, to: string) => {
    return apiClient.get<DeploymentMarker[]>(`${ADMIN_ADS_URL()}/deployment-markers`, {
      params: { from, to },
    });
  },

  /** G1-2: 배포 마커 등록 */
  createDeploymentMarker: (data: CreateDeploymentMarkerRequest) => {
    return apiClient.post<{ id: number }>(`${ADMIN_ADS_URL()}/deployment-markers`, data);
  },

  /** G1-2: 배포 마커 삭제 */
  deleteDeploymentMarker: (id: number) => {
    return apiClient.delete(`${ADMIN_ADS_URL()}/deployment-markers/${id}`);
  },

  /** G1-2: 광고ID별 일별 집계 */
  getIdAggregation: (position: AdPosition, days: number = 14) => {
    return apiClient.get<IdAggregation>(`${ADMIN_ADS_URL()}/id-aggregation`, {
      params: { position, days },
    });
  },

  // ==================== AARRR 서비스 분석 ====================

  /** Acquisition: 신규 가입자 추이 + 플랫폼별 비율 */
  getAcquisition: () => {
    return apiClient.get<AcquisitionData>(`${ANALYTICS_URL()}/acquisition`);
  },

  /** Activation: 첫 투표 완료율, 첫 설문 작성율, 활성화 소요시간 */
  getActivation: () => {
    return apiClient.get<ActivationData>(`${ANALYTICS_URL()}/activation`);
  },

  /** Retention: DAU/WAU/MAU + 코호트 리텐션 */
  getRetention: () => {
    return apiClient.get<RetentionData>(`${ANALYTICS_URL()}/retention`);
  },

  /** 일별 코호트 리텐션 조회 */
  getRetentionCohort: (from: string, to: string, type: RetentionType = 'ALL', maxDay: number = 30) => {
    return apiClient.get<DailyCohortRetentionResponse>(`${ANALYTICS_URL()}/retention/cohort`, {
      params: { from, to, type, maxDay },
    });
  },

  /** 특정 가입일 코호트 유저 목록 */
  getCohortUsers: (date: string) => {
    return apiClient.get<CohortUserItem[]>(`${ANALYTICS_URL()}/retention/cohort/${date}/users`);
  },

  /** 개별 유저 리텐션 상세 조회 */
  getUserRetention: (userId: number) => {
    return apiClient.get<UserRetentionDetail>(`${ANALYTICS_URL()}/retention/user/${userId}`);
  },

  // ==================== 광고 데이터 분석 ====================

  /** 광고 데이터 종합 분석 */
  getAdDataAnalysis: (period: string = '7D') => {
    return apiClient.get(`${ADMIN_ADS_URL()}/data-analysis`, { params: { period } });
  },

  /** 광고 시청 추이 (대시보드용) */
  getAdWatchTrend: (period: string = '30D') => {
    return apiClient.get(`${ADMIN_ADS_URL()}/watch-trend`, { params: { period } });
  },

  /** 리워드 광고 성과 분석 */
  getRewardPerformance: (period: string = '7D') => {
    return apiClient.get<RewardPerformanceResponse>(`${ADMIN_ADS_URL()}/reward-performance`, { params: { period } });
  },

  /** 터치포인트별 일별 매트릭스 */
  getTouchpointDailyMatrix: (from: string, to: string) => {
    return apiClient.get<TouchpointDailyMatrixResponse>(`${ADMIN_ADS_URL()}/touchpoint-daily-matrix`, {
      params: { from, to },
    });
  },

  // ==================== eCPM 유저별 광고 시청 통계 ====================

  /** eCPM 유저별 광고 시청 통계 */
  getEcpmUserStats: (periodType: string, date?: string) => {
    const params = new URLSearchParams();
    params.append('periodType', periodType);
    if (date) params.append('date', date);
    return apiClient.get<EcpmUserStatsResponse>(`${ADMIN_ADS_URL()}/ecpm/user-stats?${params.toString()}`);
  },

  // ==================== 전략 대시보드 ====================

  /** 전략 메트릭 조회 */
  getStrategyMetrics: (from: string, to: string) => {
    return apiClient.get<StrategyMetricsResponse>(`${ADMIN_ADS_URL()}/strategy/metrics`, {
      params: { from, to },
    });
  },

  /** 토스 데이터 수동 입력 */
  saveTossData: (data: TossDataRequest) => {
    return apiClient.post(`${ADMIN_ADS_URL()}/strategy/toss-data`, data);
  },

  /** 배포 마커 등록 */
  saveDeployMarker: (data: DeployMarkerRequest) => {
    return apiClient.post(`${ADMIN_ADS_URL()}/strategy/deploys`, data);
  },

  /** 배포 마커 목록 */
  getDeploys: () => {
    return apiClient.get<DeployMarkerItem[]>(`${ADMIN_ADS_URL()}/strategy/deploys`);
  },

  /** 리워드 체계 분석 (설정 스냅샷 + 타입별 일별 브레이크다운) */
  getRewardBreakdown: (from: string, to: string) => {
    return apiClient.get<RewardBreakdownResponse>(`${ADMIN_ADS_URL()}/strategy/reward-breakdown`, {
      params: { from, to },
    });
  },

  /** 조각경제 현재 스냅샷 */
  getPieceSnapshot: () => {
    return apiClient.get<PieceSnapshotResponse>(`${ADMIN_ADS_URL()}/strategy/piece-snapshot`);
  },
};
